import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Paper,
  Typography
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Launch as LaunchIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 600,
    maxWidth: '100vw'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    background: 'linear-gradient(45deg, #2e7d32 30%, #66bb6a 90%)',
    color: theme.palette.common.white
  },
  headerTitle: {
    fontWeight: 600,
    marginRight: theme.spacing(2)
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center'
  },
  content: {
    padding: theme.spacing(3),
    height: '100%',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.default
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionHeader: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center'
  },
  sectionTitle: {
    fontWeight: 600
  },
  statusChip: {
    marginLeft: theme.spacing(1)
  },
  accordionRoot: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    boxShadow: 'none',
    '&:before': {
      display: 'none'
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1)
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  categoryTitle: {
    flexGrow: 1
  },
  scoreChip: {
    fontWeight: 600
  },
  photoList: {
    maxHeight: 220,
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  photoListItem: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  notesList: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1)
  },
  emptyState: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2)
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2)
  },
  automationInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  statsPaper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`
  },
  errorButton: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.dark,
      backgroundColor: theme.palette.error.light + '10'
    }
  },
  errorDialogTitle: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  errorSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`
  },
  errorSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  codeBlock: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  aspireLink: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  componentScore: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  }
}))

const CATEGORY_NAMES = {
  0: 'Punchlist',
  1: 'Turf Health and Fertility',
  2: 'Turf Management, Edging, and Mowing',
  3: 'Bed Areas/Mulch',
  4: 'Seasonal Color/Decor',
  5: 'Perennials/Shrubs',
  6: 'Trees',
  7: 'Native/Meadow',
  8: 'Water Features',
  9: 'Irrigation',
  10: 'Other'
}

const getScoreColor = (score, theme) => {
  if (score === 'NA' || score === null || score === undefined) return theme.palette.grey[500];
  const numScore = typeof score === 'number' ? score : parseInt(score, 10);
  if (numScore >= 4) return theme.palette.success.main;
  if (numScore >= 3) return theme.palette.warning.main;
  return theme.palette.error.main;
}

const statusColor = (status, theme) => {
  switch (status) {
    case 'success':
      return theme.palette.success.main;
    case 'failed':
      return theme.palette.error.main;
    case 'processing':
      return theme.palette.info.main;
    default:
      return theme.palette.warning.main;
  }
}

const buildInitialState = (submission) => {
  const source = submission && typeof submission === 'object' ? submission : {};
  const payloadSnapshot = source.payloadSnapshot || {};

  return {
    propertyId: source.propertyId || payloadSnapshot.propertyId || '',
    jobSiteName: source.jobSiteName || payloadSnapshot.jobSiteName || '',
    auditName: source.auditName || payloadSnapshot.auditName || '',
    categories: source.categories || payloadSnapshot.categories || [],
    stats: source.stats || payloadSnapshot.stats || null,
    mpgqaPayload: source.mpgqaPayload || null
  };
}

const MpgqaSubmissionDetailDrawer = ({ open, submission, onClose, onResubmit, resubmitting }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [formState, setFormState] = useState(buildInitialState(submission));
  const [localError, setLocalError] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  useEffect(() => {
    setFormState(buildInitialState(submission));
    setLocalError(null);
    setErrorModalOpen(false);
  }, [submission]);

  const handleResubmitClick = useCallback(async () => {
    if (!submission || !submission._id) return;
    setLocalError(null);

    const overrides = {
      propertyId: formState.propertyId,
      jobSiteName: formState.jobSiteName,
      categories: formState.categories
    };

    try {
      await onResubmit(submission._id, { overrides });
    } catch (error) {
      setLocalError(error?.message || 'Failed to resubmit the MPG QA.');
    }
  }, [formState, onResubmit, submission]);

  const attempts = useMemo(() => {
    if (!submission) return 0;
    const count = submission.resubmissionCount || 0;
    return count + 1;
  }, [submission]);

  const automationDetails = useMemo(() => {
    if (!submission) return null;
    const automation = submission.automation || {};
    return {
      startedAt: automation.startedAt ? moment(automation.startedAt) : null,
      completedAt: automation.completedAt ? moment(automation.completedAt) : null
    };
  }, [submission]);

  const statusChip = useMemo(() => {
    if (!submission) return null;
    const themeColor = statusColor(submission.status, theme);
    return (
      <Chip
        size="small"
        label={submission.status ? submission.status.toUpperCase() : 'UNKNOWN'}
        style={{ backgroundColor: themeColor, color: '#fff' }}
        className={classes.statusChip}
      />
    );
  }, [submission, classes.statusChip, theme]);

  // Get the transformed categories for display
  const transformedCategories = useMemo(() => {
    const mpgqa = submission?.mpgqaPayload || [];
    return mpgqa.map((cat, index) => ({
      index,
      name: cat?.name || CATEGORY_NAMES[index] || `Category ${index}`,
      score: cat?.score,
      notes: cat?.notes || [],
      photos: cat?.photos || [],
      hasData: cat !== null
    })).filter(cat => cat.hasData);
  }, [submission]);

  // Original app categories (for more detail)
  const appCategories = useMemo(() => {
    return formState.categories || [];
  }, [formState.categories]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <div>
          <Typography variant="h6" className={classes.headerTitle}>
            {submission?.jobSiteName || 'MPG QA Submission'}
          </Typography>
          {submission?.propertyId && (
            <Typography variant="body2">Property ID: {submission.propertyId}</Typography>
          )}
        </div>
        <div className={classes.headerActions}>
          {statusChip}
          <IconButton onClick={onClose} color="inherit">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className={classes.content}>
        {localError && (
          <Box mb={2}>
            <Alert severity="error" onClose={() => setLocalError(null)}>{localError}</Alert>
          </Box>
        )}

        {/* Aspire Link */}
        {submission?.aspireAuditUrl && (
          <Box mb={2}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="body2">
                Successfully submitted to Aspire
              </Typography>
              <Link
                href={submission.aspireAuditUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.aspireLink}
              >
                View in Aspire <LaunchIcon fontSize="small" />
              </Link>
              {submission.aspireAuditId && (
                <Typography variant="caption" display="block" color="textSecondary">
                  Audit ID: {submission.aspireAuditId}
                </Typography>
              )}
            </Alert>
          </Box>
        )}

        {/* Submission Details */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>Submission Details</Typography>
          </div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Property ID</Typography>
              <Typography variant="body1">{formState.propertyId || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Job Site Name</Typography>
              <Typography variant="body1">{formState.jobSiteName || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">Audit Name</Typography>
              <Typography variant="body1">{formState.auditName || '—'}</Typography>
            </Grid>
          </Grid>
        </div>

        {/* Category Scores - Transformed for Aspire */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Category Scores (Submitted to Aspire)
            </Typography>
          </div>

          {transformedCategories.length === 0 ? (
            <Typography variant="body2" className={classes.emptyState}>
              No categories were submitted to Aspire.
            </Typography>
          ) : (
            transformedCategories.map((cat) => (
              <Accordion key={cat.index} className={classes.accordionRoot}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                  <div className={classes.categoryHeader}>
                    <div className={classes.categoryTitle}>
                      <Typography variant="subtitle2">{cat.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Index {cat.index}
                      </Typography>
                    </div>
                    <Chip
                      size="small"
                      label={cat.score !== null && cat.score !== undefined ? cat.score : 'NA'}
                      className={classes.scoreChip}
                      style={{
                        backgroundColor: getScoreColor(cat.score, theme),
                        color: '#fff'
                      }}
                    />
                  </div>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  {/* Notes */}
                  {cat.notes.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="caption" color="textSecondary">Notes</Typography>
                      <div className={classes.notesList}>
                        {cat.notes.map((note, idx) => (
                          <Typography key={idx} variant="body2" style={{ marginBottom: 4 }}>
                            • {note}
                          </Typography>
                        ))}
                      </div>
                    </Box>
                  )}

                  {/* Photos */}
                  {cat.photos.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Photos ({cat.photos.length})
                      </Typography>
                      <Paper elevation={0} className={classes.photoList}>
                        <List dense>
                          {cat.photos.map((photo, photoIndex) => (
                            <ListItem key={photoIndex} className={classes.photoListItem}>
                              <ListItemText
                                primary={photo.fileName || `Photo ${photoIndex + 1}`}
                                secondary={photo.notes || 'No caption'}
                              />
                              {photo.s3Url && (
                                <IconButton
                                  edge="end"
                                  component={Link}
                                  href={photo.s3Url}
                                  target="_blank"
                                  rel="noopener"
                                >
                                  <LaunchIcon fontSize="small" />
                                </IconButton>
                              )}
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Box>
                  )}

                  {cat.notes.length === 0 && cat.photos.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      No additional details for this category.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </div>

        {/* Original App Categories - Detailed View */}
        {appCategories.length > 0 && (
          <div className={classes.section}>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Original Category Data (From Mobile App)
              </Typography>
            </div>

            {appCategories.map((cat, idx) => {
              const hasSelections = cat.selections && Object.keys(cat.selections).length > 0;
              const score = cat.score || {};

              return (
                <Accordion key={cat.id || idx} className={classes.accordionRoot}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                    <div className={classes.categoryHeader}>
                      <div className={classes.categoryTitle}>
                        <Typography variant="subtitle2">{cat.title || cat.id}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {score.completedComponents || 0} of {score.totalComponents || 0} components •
                          Score: {score.totalScore || 0}/{score.maxScore || 0}
                        </Typography>
                      </div>
                      {hasSelections ? (
                        <CheckCircleIcon style={{ color: theme.palette.success.main }} fontSize="small" />
                      ) : (
                        <CancelIcon style={{ color: theme.palette.grey[400] }} fontSize="small" />
                      )}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {/* Selections */}
                    {hasSelections && (
                      <Box mb={2}>
                        <Typography variant="caption" color="textSecondary">Component Selections</Typography>
                        <div className={classes.notesList}>
                          {Object.entries(cat.selections).map(([componentId, selection]) => (
                            <div key={componentId} className={classes.componentScore}>
                              <Typography variant="body2">
                                {componentId}: <strong>{selection}</strong>
                              </Typography>
                            </div>
                          ))}
                        </div>
                      </Box>
                    )}

                    {/* Notes */}
                    {cat.notes && Object.keys(cat.notes).length > 0 && (
                      <Box mb={2}>
                        <Typography variant="caption" color="textSecondary">Notes</Typography>
                        <div className={classes.notesList}>
                          {Object.entries(cat.notes).map(([componentId, note]) => (
                            <Typography key={componentId} variant="body2" style={{ marginBottom: 4 }}>
                              <strong>{componentId}:</strong> {note}
                            </Typography>
                          ))}
                        </div>
                      </Box>
                    )}

                    {!hasSelections && (!cat.notes || Object.keys(cat.notes).length === 0) && (
                      <Typography variant="body2" color="textSecondary">
                        No data recorded for this category.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        )}

        {/* Automation Status */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>Automation Status</Typography>
          </div>
          <Typography variant="body2" className={classes.automationInfo}>
            Attempts: {attempts}
          </Typography>
          {automationDetails?.startedAt && (
            <Typography variant="body2" className={classes.automationInfo}>
              Last Started: {automationDetails.startedAt.format('MMM D, YYYY h:mm A')}
            </Typography>
          )}
          {automationDetails?.completedAt && (
            <Typography variant="body2" className={classes.automationInfo}>
              Completed: {automationDetails.completedAt.format('MMM D, YYYY h:mm A')}
            </Typography>
          )}
          {submission?.lastResubmittedBy && (
            <Typography variant="body2" className={classes.automationInfo}>
              Last Resubmitted By: {submission.lastResubmittedBy.displayName || submission.lastResubmittedBy.name || submission.lastResubmittedBy.email}
            </Typography>
          )}
          {submission?.status === 'failed' && submission?.failure && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ErrorIcon />}
              onClick={() => setErrorModalOpen(true)}
              className={classes.errorButton}
            >
              View Error Details
            </Button>
          )}
        </div>

        {/* Stats */}
        {formState.stats && (
          <div className={classes.section}>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>Stats Snapshot</Typography>
            </div>
            <Paper elevation={0} className={classes.statsPaper}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Categories</Typography>
                  <Typography variant="body1">{formState.stats.categoryCount || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Scored</Typography>
                  <Typography variant="body1">{formState.stats.scoredCategoryCount || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Photos</Typography>
                  <Typography variant="body1">{formState.stats.photoCount || 0}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </div>
        )}

        <Divider />

        <div className={classes.actions}>
          <Button onClick={onClose} variant="outlined" color="default">
            Close
          </Button>
          <div className={classes.actionButtons}>
            <Button
              onClick={handleResubmitClick}
              color="primary"
              variant="contained"
              disabled={resubmitting}
            >
              {resubmitting ? 'Resubmitting…' : 'Resubmit to Aspire'}
            </Button>
          </div>
        </div>
      </div>

      {/* Error Details Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className={classes.errorDialogTitle}>
          <ErrorIcon />
          <span>Error Details</span>
        </DialogTitle>
        <DialogContent dividers>
          {submission?.failure ? (
            <>
              {/* Error Code */}
              <div className={classes.errorSection}>
                <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                  Error Code
                </Typography>
                <Typography variant="body2" color="error">
                  {submission.failure.code || 'UNKNOWN'}
                </Typography>
              </div>

              {/* Error Message */}
              {submission.failure.message && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Message
                  </Typography>
                  <Typography variant="body2">
                    {submission.failure.message}
                  </Typography>
                </div>
              )}

              {/* Error Type & Stage */}
              {(submission.failure.errorType || submission.failure.errorStage) && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Error Classification
                  </Typography>
                  <Grid container spacing={2}>
                    {submission.failure.errorType && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Type</Typography>
                        <Typography variant="body2">{submission.failure.errorType}</Typography>
                      </Grid>
                    )}
                    {submission.failure.errorStage && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Stage</Typography>
                        <Typography variant="body2">{submission.failure.errorStage}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </div>
              )}

              {/* Retryable Flag */}
              {submission.failure.retryable !== undefined && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Retryable
                  </Typography>
                  <Chip
                    label={submission.failure.retryable ? 'YES' : 'NO'}
                    color={submission.failure.retryable ? 'primary' : 'default'}
                    size="small"
                  />
                  {submission.failure.retryable && (
                    <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
                      This error can be retried. Try resubmitting with corrected data.
                    </Typography>
                  )}
                </div>
              )}

              {/* Meta Information */}
              {submission.failure.meta && Object.keys(submission.failure.meta).length > 0 && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Additional Metadata
                  </Typography>
                  <div className={classes.codeBlock}>
                    {JSON.stringify(submission.failure.meta, null, 2)}
                  </div>
                </div>
              )}

              {/* Error Stack Trace */}
              {submission.failure.errorStack && (
                <div className={classes.errorSection}>
                  <Typography variant="subtitle2" className={classes.errorSectionTitle}>
                    Stack Trace
                  </Typography>
                  <div className={classes.codeBlock}>
                    {submission.failure.errorStack}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No error details available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

MpgqaSubmissionDetailDrawer.propTypes = {
  open: PropTypes.bool,
  submission: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onResubmit: PropTypes.func.isRequired,
  resubmitting: PropTypes.bool
};

MpgqaSubmissionDetailDrawer.defaultProps = {
  open: false,
  submission: null,
  resubmitting: false
};

export default MpgqaSubmissionDetailDrawer;
